import React, { useEffect, useMemo, useRef, useState } from "react"
import { Box, Grid, MenuItem, Typography } from "@mui/material"
import { MRT_ColumnDef, MRT_RowSelectionState, MRT_TableInstance } from "material-react-table"
import { faFilePdf, faFileArrowDown } from "@fortawesome/pro-light-svg-icons"
import { makeStyles } from "tss-react/mui"
import { useAppLocalization } from "../../../../../localization/components/AppLocalizationProvider"
import { useApiQueryTable } from "../../../../../../core/api/components/ApiQueryTable"
import { useApiQuery } from "../../../../../../core/api/useApiQuery"
import {
    MeetingServiceOrdersForCompanyResponse,
    fetchMeetingServiceOrdersForCompany,
    CompanyReportsMeetingServiceQuery,
} from "../meetingServiceApi"
import { MeetingServiceOrder } from "../meetingServiceTypes"
import { GROW_CELL_CLASS, useGridLayoutStyles } from "../../../../../../shared/components/table/useGridLayoutStyles"
import { COLUMN_SIZE } from "../../../../../../shared/constants/table/config"
import { LocalizedStrict } from "../../../../../localization/components/AppLocalized"
import { PageHeader } from "../../../../../../shared/components/pageHeader/PageHeader"
import { Page } from "../../../../../../shared/components/pageHeader/PageHeaderApi"
import {
    Breadcrumb,
    BreadcrumbLink,
    StandardBreadcrumbs,
} from "../../../../../../shared/components/links/StandardBreadcrumbs"
import { COMPANY_REPORTS_ROUTE } from "../../../../../../constants/routes"
import { ActionBarItem, FloatingActionBar } from "../../../../../../shared/components/table/FloatingActionBar"
import { useCurrentCompany } from "../../../../hooks/useCurrentCompany"
import { StandardDateFilter } from "../../../../../../shared/components/table/StandardDateFilter"
import { StandardTwoLinesCell } from "../../../../../../shared/components/table/StandardTwoLinesCell"
import {
    defaultMeetingServiceFilterOptionsList,
    FiltersDrawerMeetingService,
    MeetingServiceFilterProps,
} from "../../../../../supplier/reporting/accounting/meetingService/components/FiltersDrawerMeetingService"
import useActiveFiltersCount from "../../../../../supplier/reporting/accounting/meetingService/hooks/useActiveFiltersCount"
import { OrderIdInput } from "../../../../../supplier/reporting/accounting/components/inputFilters/OrderIdInput"
import { StandardFilterButton } from "../../../../../../shared/components/table/StandardFilterButton"
import { StandardTableFilter } from "../../../../../../shared/components/table/StandardTableFilters"
import { useInitialDateFilters } from "../../../../../../shared/components/table/useInitialDateFilters"
import { StandardDateRangeFilter } from "../../../../../../shared/components/table/StandardDateRangeFilter"
import { useAppSelector } from "../../../../../store/storeHooks"
import { selectEffectiveUserId } from "../../../../../context/contextSelectors"
import { MeetingServiceOrderDrawer } from "../../../../../../shared/components/meetingServiceOrderDrawer/MeetingServiceOrderDrawer"
import { isOrderRowControlClick } from "../../../../../../shared/components/meetingServiceOrderDrawer/orderDrawerRow"
import { ChatMessageAuthorRole, UserType } from "../../../../../../shared/components/orderChat/orderChatTypes"

const useStyles = makeStyles()(() => ({
    table: {
        ".MuiTableCell-root": {
            paddingLeft: "1rem",
            paddingRight: "1rem",
        },
        ".Mui-TableHeadCell-Content-Labels": {
            paddingLeft: 0,
        },
    },
}))

const selectMeetingService = (data: MeetingServiceOrdersForCompanyResponse) => data?.orders || []

export function CompanyMeetingService() {
    const { classes } = useStyles()
    const { classes: gridClasses } = useGridLayoutStyles()
    const { companyId } = useCurrentCompany()
    const userId = useAppSelector(selectEffectiveUserId)

    const { l10n } = useAppLocalization()

    const [rowSelection, setRowSelection] = useState<MRT_RowSelectionState>({})
    const [selectedRows, setSelectedRows] = useState<MeetingServiceOrder[]>()
    const [drawerOpen, setDrawerOpen] = useState(false)
    const [drawerOrder, setDrawerOrder] = useState<MeetingServiceOrder | undefined>(undefined)
    const { startDate, endDate } = useInitialDateFilters()
    const [filtersList, setFiltersList] = useState<MeetingServiceFilterProps>({
        ...defaultMeetingServiceFilterOptionsList,
        startBookedDate: startDate,
        endBookedDate: endDate,
    })

    const orderIds = selectedRows ? selectedRows.map((order) => order.id) : []
    const { activeCount } = useActiveFiltersCount(filtersList)

    const tableInstanceRef = useRef<MRT_TableInstance<MeetingServiceOrder>>(null)
    useEffect(() => {
        if (Object.keys(rowSelection).length > 0) {
            setSelectedRows(tableInstanceRef.current?.getSelectedRowModel().rows.map((row) => row.original))
        } else {
            setSelectedRows(undefined)
        }
    }, [rowSelection])

    // An order id identifies the order uniquely, so the search must not be narrowed by the
    // period - the user searches by id precisely because the date is unknown.
    const isSearchingByOrderId = Boolean(filtersList.orderId)

    const meetingServiceOrdersResult = useApiQuery(fetchMeetingServiceOrdersForCompany, {
        queryName: CompanyReportsMeetingServiceQuery.MEETING_SERVICE_ORDERS_FOR_COMPANY,
        dependencies: {
            companyId,
            ...filtersList,
            startBookedDate: isSearchingByOrderId ? undefined : filtersList.startBookedDate?.toISODate(),
            endBookedDate: isSearchingByOrderId ? undefined : filtersList.endBookedDate?.toISODate(),
            startDeliveryDate: filtersList.startDeliveryDate?.toISODate(),
            endDeliveryDate: filtersList.endDeliveryDate?.toISODate(),
            shopIds: filtersList.shopIds?.join(","),
            merchantIds: filtersList.merchantIds?.join(","),
            satelliteIds: filtersList.satelliteIds?.join(","),
            buyerPartyIds: filtersList.buyerPartyIds?.join(","),
            paymentMethod: filtersList.paymentMethod?.join(","),
        },
        select: selectMeetingService,
    })

    const { ApiQueryTable, columnFactory } = useApiQueryTable(meetingServiceOrdersResult)

    const columns = useMemo<MRT_ColumnDef<MeetingServiceOrder>[]>(
        () => [
            columnFactory.idColumn({
                header: l10n.getString("company-meeting-service-table-order-id-column"),
                accessorKey: "id",
            }),
            columnFactory.dateColumn(
                {
                    header: l10n.getString("company-meeting-service-table-booked-date-column"),
                    accessorKey: "accountingDetails.bookedTime",
                },
                (row) => row?.accountingDetails?.bookedTime,
            ),
            columnFactory.datetimeColumn(
                {
                    header: l10n.getString("company-meeting-service-table-delivery-time-column"),
                    accessorKey: "deliveryDetails.time",
                },
                (row) => row?.deliveryDetails?.time,
            ),
            columnFactory.nameColumn({
                header: l10n.getString("company-meeting-service-table-employee-column"),
                accessorKey: "customer.displayName",
                size: COLUMN_SIZE.XL,
                muiTableHeadCellProps: { className: GROW_CELL_CLASS },
                muiTableBodyCellProps: { className: GROW_CELL_CLASS },
                Cell: ({ row }) => (
                    <StandardTwoLinesCell
                        title={
                            <>
                                <Typography variant="body2" component="span" textTransform="uppercase">
                                    {row.original.customer.initials}
                                    {", "}
                                </Typography>
                                <Typography variant="body2" component="span">
                                    {row.original.customer.displayName}
                                </Typography>
                            </>
                        }
                        subtitle1={row.original.meetingDetails.meetingName}
                    />
                ),
            }),
            columnFactory.moneyColumn(
                {
                    header: l10n.getString("company-meeting-service-table-amount-column"),
                    id: "amount",
                    size: COLUMN_SIZE.M,
                    minSize: COLUMN_SIZE.M,
                },
                (row) => row.amount,
            ),
        ],
        [l10n, columnFactory],
    )

    const handleClearSelected = () => {
        setSelectedRows(undefined)
        setRowSelection({})
    }

    return (
        <>
            <LocalizedStrict id="company-meeting-service-header" attrs={{ title: true }}>
                <PageHeader page={Page.COMPANY_MEETING_SERVICE} title="Meeting Service">
                    <StandardBreadcrumbs>
                        <LocalizedStrict id="company-nav-item-home">
                            <BreadcrumbLink to={COMPANY_REPORTS_ROUTE}>Home</BreadcrumbLink>
                        </LocalizedStrict>
                        <LocalizedStrict id="accounting-pos-table-header-nav-item-accounting">
                            <Breadcrumb>Accounting</Breadcrumb>
                        </LocalizedStrict>
                        <LocalizedStrict id="company-meeting-service-title">
                            <Breadcrumb>Meeting Service</Breadcrumb>
                        </LocalizedStrict>
                    </StandardBreadcrumbs>
                </PageHeader>
            </LocalizedStrict>

            <Box className={classes.table}>
                <ApiQueryTable
                    layoutMode="grid"
                    muiTablePaperProps={{ className: gridClasses.pinned }}
                    tableInstanceRef={tableInstanceRef}
                    columns={columns}
                    onRowSelectionChange={setRowSelection}
                    enableRowSelection
                    initialState={{ density: "spacious" }}
                    muiTableBodyRowProps={({ row }) => ({
                        sx: { cursor: "pointer" },
                        onClick: (event) => {
                            if (isOrderRowControlClick(event.target)) return
                            setDrawerOrder(row.original)
                        },
                    })}
                    renderRowActionMenuItems={({ row, closeMenu }) => [
                        <LocalizedStrict id="company-meeting-service-table-show-receipt">
                            <MenuItem
                                onClick={() => {
                                    closeMenu()
                                    // handleShowReceipt(row.original.id)
                                }}
                                key={`show-receipt-${row.original.id}`}
                            >
                                Show receipt
                            </MenuItem>
                        </LocalizedStrict>,
                    ]}
                    renderToolbarInternalActions={({ table }) => (
                        <StandardTableFilter variant="right" table={table}>
                            <StandardFilterButton onClick={() => setDrawerOpen(true)} activeCount={activeCount} />
                        </StandardTableFilter>
                    )}
                    renderTopToolbarCustomActions={() => (
                        <StandardTableFilter variant="left">
                            <StandardDateRangeFilter
                                value={{
                                    startDate: filtersList.startBookedDate!,
                                    endDate: filtersList.endBookedDate!,
                                }}
                                onChange={(newValue) => {
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        startBookedDate: newValue.startDate,
                                        endBookedDate: newValue.endDate,
                                    }))
                                }}
                            />
                            <OrderIdInput
                                value={filtersList.orderId ?? ""}
                                onChange={(orderId) => {
                                    setFiltersList((prev) => ({
                                        ...prev,
                                        orderId,
                                    }))
                                }}
                            />
                        </StandardTableFilter>
                    )}
                    state={{
                        rowSelection,
                    }}
                />
            </Box>
            {selectedRows && (
                <FloatingActionBar selectedItemsLength={selectedRows.length} handleCloseActionBar={handleClearSelected}>
                    <LocalizedStrict
                        id="company-meeting-service-table-floating-action-bar-receipts"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem icon={faFilePdf} title="Receipts" onClick={() => {}} />
                    </LocalizedStrict>

                    <LocalizedStrict
                        id="company-meeting-service-table-floating-action-bar-export"
                        attrs={{ title: true }}
                    >
                        <ActionBarItem icon={faFileArrowDown} title="Export" onClick={() => {}} />
                    </LocalizedStrict>
                </FloatingActionBar>
            )}

            <FiltersDrawerMeetingService
                setFiltersList={setFiltersList}
                drawerOpen={drawerOpen}
                setDrawerOpen={setDrawerOpen}
                filtersList={filtersList}
            />

            {drawerOrder && (
                <MeetingServiceOrderDrawer
                    open
                    order={drawerOrder}
                    viewerRole={ChatMessageAuthorRole.CUSTOMER}
                    sender={userId && companyId ? { type: UserType.USER, companyId, userId } : undefined}
                    onClose={() => setDrawerOrder(undefined)}
                />
            )}
        </>
    )
}
